import {useIntl} from "react-intl";
import {useCallback, useContext} from "react";
import CountryContext from "../CountryContext";
import useCountryAxiosInstance from "./useCountryAxiosInstance";
import {NotificationsContext} from "../ui-components/Notifications";

const useDispatchServiceChecklist = (serviceId) => {
  const intl = useIntl();
  const {push} = useContext(NotificationsContext);
  const { country } = useContext(CountryContext);
  const countryApiRequest = useCountryAxiosInstance(country?.code);

  return useCallback(
    async (prev, curr) => {
      if (!prev && !curr) {
        // case A: no checklists were provided --> create a new one and return
        return await countryApiRequest.post('/services_checklists', {
          id_service: serviceId,
          des_title: intl.formatMessage({id: 'new_checklist_title'}),
          des_description: intl.formatMessage({id: 'new_checklist_description'}),
          flg_active: false,
          dependencies: [],
          r_perm: ['public'],
          w_perm: ['admin'],
        })
      }

      const {id, items, ...reducedChecklist} = curr;

      let refreshed, toUpsert, toDelete;
      if (!prev) {
        // case B: only current checklist was provided --> duplication
        refreshed = await countryApiRequest.post(`/services_checklists`, reducedChecklist);
        toUpsert = items.map((i) => {
          const {id, ...rest} = i;
          return {...rest, id_checklist: refreshed.id}
        });
        toDelete = []
      }
      else {
        // case C: checklist was provided --> update existing checklist and items
        refreshed = await countryApiRequest.put(`/services_checklists/${id}`, reducedChecklist);
        toUpsert = items;
        toDelete = prev.items
          .filter((pi) => !items.some((ci) => ci.id === pi.id))
          .map((pi) => pi.id);

        // TEMPORARY: some users have reported cases in which all activities were deleted (but the checklist was still
        // present). As for now, case C seems to be the only way this can occur, but we couldn't figure out why. In
        // order to avoid this behavior, we are going to prevent it manually - if the users wants to delete all
        // activities, they must delete the whole checklist instead
        if (toDelete.length > 0 && toDelete.length === prev.items.length) {
          toDelete = [];  // don't delete anything
          push({
            type: 'warning',
            title: intl.formatMessage({id: 'prevent_checklist_activities_removal_warning'})
          });
        }
      }
      refreshed['items'] = await countryApiRequest.post(
        `/services_checklists_items/update`,
        {items: toUpsert, to_delete: toDelete}
      );

      return refreshed;
    },
    [serviceId, countryApiRequest, intl],
  );
}

export default useDispatchServiceChecklist;